<template>
  <div>
    <v-navigation-drawer
      v-model="showMenu"
      :clipped="$vuetify.breakpoint.lgAndUp"
      app
      :mini-variant="menuFolded"
    >
      <div class="d-flex flex-column full-height">
        <main-menu :menu-folded="menuFolded" @unfold-menu="menuFolded = false"></main-menu>

        <template v-if="hasWaitingUpdate">
          <div class="mt-auto full-width d-flex">
            <v-btn
              small
              class="mx-auto mb-2"
              @click="openUpdateDialog"
              :icon="menuFolded"
              :outlined="!menuFolded"
            >
              <div class="update-dot my-auto" :class="{ 'mr-4': !menuFolded }"></div>
              <div v-show="!menuFolded">Uppdatering tillgänglig</div>
            </v-btn>
          </div>
        </template>

        <div v-if="user?.is_support" class="d-flex" :class="{ 'mt-0': hasWaitingUpdate, 'mt-auto': !hasWaitingUpdate }">
          <div class="text-sm pa-1 mx-auto">{{ supportInfo }}</div>
        </div>
      </div>
    </v-navigation-drawer>

    <v-app-bar
      :clipped-left="$vuetify.breakpoint.lgAndUp"
      app
      color="primary darken-2"
      dark
    >
      <v-app-bar-nav-icon
        @click.stop="toggleMenu"
        class="mr-2 mr-md-0"
      ></v-app-bar-nav-icon>
      <v-toolbar-title style="width: auto" class="ml-0 px-12 hidden-sm-and-down">
        <span>
          <router-link to="/">
            <v-img
              :src="require('@/assets/logos/logo-white.svg')"
              contain
              :height="42"
              :width="160"
              alt="CampingOnline"
            ></v-img>
          </router-link>
        </span>
      </v-toolbar-title>

      <v-spacer class="d-block d-md-none"></v-spacer>

      <div class="mx-0 d-flex flex-md-grow-1">
        <app-search></app-search>
      </div>

      <v-spacer class="d-none d-md-block"></v-spacer>

      <v-menu
        v-model="showDraftMenu"
        :close-on-content-click="false"
        :nudge-width="200"
        offset-y
      >
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <simple-tooltip tooltip="Utkast bokningar">
              <v-btn
                icon
                class="mr-4"
              >
                <v-badge
                  color="secondary"
                  :content="bookingDrafts.length === 0 ? '0' : bookingDrafts.length"
                >
                  <v-icon>mdi-book-edit-outline</v-icon>
                </v-badge>
              </v-btn>
            </simple-tooltip>
          </div>
        </template>
        <drafts-list v-if="showDraftMenu"></drafts-list>
      </v-menu>

      <v-btn icon @click.stop="userMenuIsOpen = !userMenuIsOpen" class="mx-2 mx-md-1">
        <v-avatar item :color="user.color">
          <span class="white--text" v-text="user.initials"></span>
        </v-avatar>
      </v-btn>
    </v-app-bar>

    <user-navigation-drawer :is-open="userMenuIsOpen" @set-is-open="setUserMenuIsOpen"></user-navigation-drawer>
  </div>
</template>

<style scoped>
.update-dot {
  width: 12px;
  height: 12px;
  background-color: #4CAF50;
  border-radius: 50%;
  animation: pulse-slow 2.5s ease-in-out infinite;
}

@keyframes pulse-slow {
  0%, 100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.6);
  }
  50% {
    opacity: 0.6;
    box-shadow: 0 0 0 6px rgba(76, 175, 80, 0);
  }
}
</style>

<script>
import { mapGetters, mapState } from 'vuex'

import AppSearch from './AppSearch'
import UserNavigationDrawer from './UserNavigationDrawer'
import MainMenu from './MainMenu.vue'
import DraftsList from '@/components/booking/lists/DraftsList.vue'
import SimpleTooltip from '@/components/common/SimpleTooltip.vue'

export default {
  name: 'AppNavigation',
  components: {
    AppSearch,
    UserNavigationDrawer,
    MainMenu,
    DraftsList,
    SimpleTooltip
  },
  data: () => ({
    showMenu: false,
    menuHistory: false,
    userMenuIsOpen: false,
    menuFolded: false,
    showDraftMenu: false,
    userFoldChoice: false // om användaren har togglat menyn till ikonläge, förutom på kartsidan
  }),
  computed: {
    ...mapGetters(
      {
        bookingDrafts: 'booking/bookingDrafts',
        activeAuthSession: 'user/activeAuthSession'
      }
    ),
    ...mapState(
      {
        hasWaitingUpdate: state => state.hasWaitingUpdate
      }
    ),
    domain: function () {
      return this.activeAuthSession?.domain
    },
    namespace: function () {
      return this.user?.namespace
    },
    user: function () {
      return this.activeAuthSession?.user
    },
    supportInfo: function () {
      const infoItems = [this.user?.name, this.domain?.name, this.namespace]
      return infoItems.filter(item => !!item).join(' - ')
    },
    currentRoute: function () {
      return this.$route.name
    },
    isMapPage: function () {
      return this.currentRoute === 'Map'
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    }
  },
  methods: {
    setUserMenuIsOpen: function (val) {
      this.userMenuIsOpen = val
    },
    toggleMenu: function () {
      if (this.isMobile) {
        this.showMenu = !this.showMenu
      } else {
        this.menuFolded = !this.menuFolded
      }
      if (!this.isMapPage) {
        this.userFoldChoice = this.menuFolded // om användaren togglar menyn utanför kartsidan, spara det valet
      }
    },
    openUpdateDialog: function () {
      this.$store.commit('SET_UPDATE_SOURCE', 'service-worker')
      this.$store.commit('SET_UPDATE_TYPE', 'pwa')
      this.$store.commit('SET_SHOW_UPDATE_DIALOG', true)
    }
  },
  watch: {
    isMapPage: function () {
      if (this.isMobile) {
        return false // aldrig ikonläge på mobil
      }
      if (this.isMapPage) {
        this.menuFolded = true
      } else {
        // När lämnar kartsida, använder användarens senaste toggle val från utanför kartsidan
        this.menuFolded = this.userFoldChoice
      }
    },
    isMobile: function () {
      if (this.isMobile) {
        this.menuFolded = false
        this.showMenu = false
      } else {
        this.showMenu = true
        this.menuFolded = this.isMapPage
      }
    }
  },
  created () {
    if (this.isMobile) {
      this.menuFolded = false
      this.showMenu = false
    } else {
      this.showMenu = true
      this.menuFolded = this.isMapPage
    }
  }
}
</script>
